"use client";
/**
 * useHistory + useFavorites — V4.0 Phase 2 Chantier 5
 *
 * Historique des 20 dernières recherches + favoris sauvegardés.
 * Persisté en localStorage côté client.
 */
import { useState, useCallback, useEffect } from 'react';

const HISTORY_KEY = 'voyage_search_history';
const FAVORITES_KEY = 'voyage_favorites';
const MAX_HISTORY = 20;

export interface HistoryEntry {
  id: string;         // hash de from+to+d1+d2
  from: string;
  to: string;
  d1: string;         // ISO date
  d2: string;
  budget: number;
  people: number;
  timestamp: number;   // Date.now()
}

export interface FavoriteEntry extends HistoryEntry {
  label?: string;     // nom personnalisé optionnel
}

function generateId(from: string, to: string, d1: string, d2: string): string {
  return `${from.toLowerCase()}_${to.toLowerCase()}_${d1}_${d2}`;
}

// === Historique ===

function loadHistory(): HistoryEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveHistory(entries: HistoryEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, MAX_HISTORY)));
  } catch { /* ignore */ }
}

export function useHistory() {
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistory);

  const addEntry = useCallback((entry: Omit<HistoryEntry, 'id' | 'timestamp'>) => {
    setHistory(prev => {
      const id = generateId(entry.from, entry.to, entry.d1, entry.d2);
      // Dédupliquer : supprimer l'ancien si même route+dates
      const filtered = prev.filter(e => e.id !== id);
      const next = [{ ...entry, id, timestamp: Date.now() }, ...filtered].slice(0, MAX_HISTORY);
      saveHistory(next);
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    saveHistory([]);
    setHistory([]);
  }, []);

  return { history, addEntry, clearHistory };
}

// === Favoris ===

function loadFavorites(): FavoriteEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveFavorites(entries: FavoriteEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(entries));
  } catch { /* ignore */ }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoriteEntry[]>(loadFavorites);

  const addFavorite = useCallback((entry: Omit<FavoriteEntry, 'id' | 'timestamp'>) => {
    setFavorites(prev => {
      const id = generateId(entry.from, entry.to, entry.d1, entry.d2);
      // Dédupliquer
      const filtered = prev.filter(e => e.id !== id);
      const next = [{ ...entry, id, timestamp: Date.now() }, ...filtered];
      saveFavorites(next);
      return next;
    });
  }, []);

  const removeFavorite = useCallback((id: string) => {
    setFavorites(prev => {
      const next = prev.filter(e => e.id !== id);
      saveFavorites(next);
      return next;
    });
  }, []);

  const isFavorite = useCallback((from: string, to: string, d1: string, d2: string): boolean => {
    const id = generateId(from, to, d1, d2);
    return favorites.some(e => e.id === id);
  }, [favorites]);

  return { favorites, addFavorite, removeFavorite, isFavorite };
}
